import { useEffect, useReducer, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { type FilterGA4Data, GA4EventName, trackFilterInteraction } from '@jsmdg/tracking';
import {
    Breakpoint,
    Button,
    ButtonColor,
    RenderType,
    SearchIcon,
    type SelectOption,
    useBreakpoint,
} from '@jsmdg/yoshi';
import { type FragmentContext } from '../../../../shared/types/fragmentContext';
import { NO_LOCATION_NAME } from '../../../constants/locationFilter';
import { PriceFilterDefault } from '../../../enums/priceFilterDefault';
import { formatTrackingPriceRange } from '../../../helper/formatPrice';
import { getGeoPosition } from '../../../helper/getGeoPosition';
import { trackLocateMeClicked } from '../../../helper/locationFilter.tracking';
import { toUrlSearchParameters } from '../../../helper/mapUrlParameters';
import { storageAvailable } from '../../../helper/storageHelper';
import { getActiveFilters } from '../../../hooks/getActiveFilters';
import { searchReducer, SearchReducerActionType } from '../../../reducers/searchReducer';
import { type SearchReducerValue } from '../../../types/searchReducer';
import { CategoriesSelect } from './Filters/CategoriesSelect';
import { LocationFilter } from './Filters/Location';
import { PriceFilter } from './Filters/Price';
import styles from './ExperienceFinder.module.scss';

const messages = defineMessages({
    cta: { defaultMessage: 'Erlebnis finden' },
});
const PRICE_BOUNDARY_MIN = PriceFilterDefault.Min;
const PRICE_BOUNDARY_MAX = PriceFilterDefault.Max;

type ExperienceFinderProps = {
    readonly categories: SelectOption[];
    readonly headline?: string;
    readonly mobileHeadline?: string;
};

const ExperienceFinder = ({
    categories,
    headline,
    mobileHeadline,
}: ExperienceFinderProps): JSX.Element => {
    const intl = useIntl();
    const { locale, reverseGeocode, tenantConfig } = useFragmentContext<FragmentContext>();
    const { currency } = tenantConfig;
    const isDesktop = useBreakpoint(Breakpoint.SM);
    const [category, setCategory] = useState<SelectOption>({
        label: tenantConfig.allExperiencesLabel,
        value: tenantConfig.allExperiencesPath,
    });
    const [geoLocationError, setGeoLocationError] = useState<GeolocationPositionError>();
    const [{ filter = {}, type: searchStateType }, dispatchSearch] = useReducer(searchReducer, {});
    const filterQuery = `?${toUrlSearchParameters(filter)}`;
    const titleRef = useRef<HTMLDivElement>(null);

    const geoRequestFiltersRef = useRef('');

    const handleFilterChange = (
        type: SearchReducerActionType,
        value?: SearchReducerValue,
    ): void => {
        if (type === SearchReducerActionType.GeoCoordinates) {
            geoRequestFiltersRef.current = getActiveFilters(filter, ['Location', 'distance']);
        }

        dispatchSearch({ type, value });
    };

    const updatePathAndLabel = (selection: SelectOption): void => {
        setCategory(selection);
    };

    const getCtaTrackingValue = (): string => {
        const location = filter.location
            ? `${filter.location.name || ''}_${filter.location.distance}km`
            : 'not selected';
        const { max = PRICE_BOUNDARY_MAX, min = PRICE_BOUNDARY_MIN } = filter?.price || {};
        const priceRange = formatTrackingPriceRange({
            locale,
            currencyCode: currency.code,
            min,
            max,
        });
        return `${location}_${category.label}_${priceRange}`;
    };

    const trackCtaClick = async (): Promise<void> => {
        await window.yieldToMainThread();

        const ctaGA4Data: FilterGA4Data = {
            eventName: GA4EventName.ClickButton,
            click_element: 'hero stage experience finder CTA',
            click_text: isDesktop ? 'SearchIcon' : intl.formatMessage(messages.cta),
        };

        const activeFilterString =
            category.label === tenantConfig.allExperiencesLabel
                ? getActiveFilters(filter)
                : getActiveFilters(filter, ['Category']);

        trackFilterInteraction('SetFilter', getCtaTrackingValue(), ctaGA4Data, activeFilterString);
    };

    const scrollToElement = () => {
        if (titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        if (searchStateType === SearchReducerActionType.GeoCoordinates) {
            const activeFilters = geoRequestFiltersRef.current;

            (async () => {
                let geoPosition;
                try {
                    geoPosition = await getGeoPosition();
                } catch (error) {
                    const locationPositionError = error as GeolocationPositionError;
                    if (
                        storageAvailable('sessionStorage') &&
                        window.GeolocationPositionError &&
                        locationPositionError.code ===
                            window.GeolocationPositionError.PERMISSION_DENIED
                    ) {
                        window.sessionStorage.setItem('locationApiAccepted', 'false');
                    }

                    setGeoLocationError(locationPositionError);

                    return;
                }

                if (storageAvailable('sessionStorage')) {
                    window.sessionStorage.setItem('locationApiAccepted', 'true');
                }

                let locationName;
                try {
                    const geocodeResponse = await reverseGeocode(
                        geoPosition.coords.latitude,
                        geoPosition.coords.longitude,
                    );
                    locationName = geocodeResponse.label;
                } catch {
                    locationName = NO_LOCATION_NAME;
                }

                await trackLocateMeClicked('LocateMe', locationName, activeFilters);

                dispatchSearch({
                    type: SearchReducerActionType.Location,
                    value: {
                        lat: geoPosition.coords.latitude,
                        long: geoPosition.coords.longitude,
                        name: locationName,
                        distance: 100,
                    },
                });
            })();
        }
    }, [reverseGeocode, searchStateType]);

    return (
        <>
            {(headline || mobileHeadline) && (
                <div
                    className="d-flex justify-content-start justify-content-sm-center position-relative"
                    ref={titleRef}
                >
                    <div className={classNames(styles.titleBackground, 'position-absolute')} />
                    <h2 className={classNames(styles.headline, 'fw-bold pb-2x pb-sm-2-5x')}>
                        {mobileHeadline && mobileHeadline !== headline ? (
                            <>
                                <span className="d-sm-none">{mobileHeadline}</span>
                                <span className="d-none d-sm-inline">{headline}</span>
                            </>
                        ) : (
                            headline
                        )}
                    </h2>
                </div>
            )}

            <div
                className={classNames(
                    styles.experienceFinder,
                    'grid gap-1x p-1-5x p-sm-3x position-relative',
                )}
                data-testid="experience-finder"
            >
                <div className="g-col-12 g-col-sm-12 g-col-md-11">
                    <div className="grid gap-1x">
                        <div
                            className="g-col-12 g-col-sm-4 mb-0-5x mb-sm-0"
                            data-testid="experience-finder-location-filter"
                        >
                            <LocationFilter
                                locationName={filter.location?.name}
                                onSubmit={handleFilterChange}
                                geoLocationError={geoLocationError}
                                filter={filter}
                                scrollToElement={scrollToElement}
                            />
                        </div>

                        <div className="g-col-12 g-col-sm-4 mb-0-5x mb-sm-0">
                            <CategoriesSelect
                                categories={categories}
                                onSelect={updatePathAndLabel}
                                filter={filter}
                                scrollToElement={scrollToElement}
                            />
                        </div>

                        <div className="g-col-12 g-col-sm-4 mb-0-5x mb-sm-0">
                            <PriceFilter
                                values={[null, null]}
                                onSubmit={handleFilterChange}
                                currencyCode={currency.code}
                                filter={filter}
                                scrollToElement={scrollToElement}
                            />
                        </div>
                    </div>
                </div>

                <div className="g-col-12 g-col-md-1">
                    <Button
                        href={`${category.value}${filterQuery}`}
                        className={classNames(styles.button, 'p-sm-2x w-100')}
                        onClick={trackCtaClick}
                        color={ButtonColor.Brand}
                        as={RenderType.Link}
                        data-testid="experience-finder-search-call-to-action"
                        internal
                        aria-label={intl.formatMessage(messages.cta)}
                        a11yText={intl.formatMessage(messages.cta)}
                    >
                        <SearchIcon ariaHidden size={28} className="d-none d-md-block" />
                        <span className="d-md-none">{intl.formatMessage(messages.cta)}</span>
                    </Button>
                </div>
            </div>
        </>
    );
};

export { ExperienceFinder };
